import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { flushOutbox } from './push';
import { pendingCount, listErrors } from './outbox';
import { getMeta } from './db';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const { authFetch } = useAuth();
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [pending, setPending] = useState(0);
  const [errors, setErrors] = useState([]);
  const [lastSyncAt, setLastSyncAt] = useState(undefined);
  const busy = useRef(false);
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;

  const refresh = useCallback(async () => {
    try {
      setPending(await pendingCount());
      setErrors(await listErrors());
      setLastSyncAt(await getMeta('lastSync:appointments'));
    } catch (_) { /* swallow: leave state unchanged on failure */ }
  }, []);

  const sync = useCallback(async () => {
    if (busy.current || !authFetchRef.current) return;
    busy.current = true;
    try {
      if ((typeof navigator === 'undefined' || navigator.onLine)) {
        await flushOutbox(authFetchRef.current);
      }
    } catch (_) { /* swallow: refresh still runs */ }
    finally {
      busy.current = false;
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    sync();
    const goOnline = () => { setOnline(true); sync(); };
    const goOffline = () => setOnline(false);
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [sync]);

  return (
    <OfflineContext.Provider value={{ online, pending, errors, lastSyncAt, refresh: sync }}>
      {children}
    </OfflineContext.Provider>
  );
}

export const useOffline = () => useContext(OfflineContext);
